import { useEffect, useMemo, useState } from 'react';

import { AllocationType, Strategy, StrategyType } from '@ndvr/portfolio-api';

import { isBlackrockStrategy, isSelectableStrategy } from '../../../strategies/strategy-utils';

/** BlackRock strategies are SHARED on the backend but get their own tab in the add-strategy dialog. */
export const BLACKROCK_TAB = 'BLACKROCK';
export type StrategyTabKey = StrategyType | typeof BLACKROCK_TAB;

const matchesTab = (strategy: Strategy, tab: StrategyTabKey): boolean =>
    tab === BLACKROCK_TAB
        ? isBlackrockStrategy(strategy)
        : strategy.strategyType === tab && !isBlackrockStrategy(strategy);

export function useStrategyFilter(
    availableStrategies: Strategy[],
    strategyAllocations: Array<{ strategyGUID: string; weight: string }>,
    allocationType: AllocationType,
    initialFilter: StrategyTabKey = StrategyType.CORE,
) {
    const [strategyTypeFilter, setStrategyTypeFilter] = useState<StrategyTabKey>(initialFilter);

    // Auto-switch tab if on "My Strategies" when allocation type is SHARED
    useEffect(() => {
        if (allocationType === AllocationType.SHARED && strategyTypeFilter === StrategyType.PERSONAL) {
            setStrategyTypeFilter(StrategyType.SHARED);
        }
    }, [allocationType, strategyTypeFilter]);

    const availableStrategiesToAdd = useMemo(() => {
        return availableStrategies.filter((strategy) => {
            if (!isSelectableStrategy(strategy)) return false;
            // Filter out already added strategies
            if (strategyAllocations.some((sa) => sa.strategyGUID === strategy.strategyGUID)) {
                return false;
            }
            // Filter by strategy type tab
            if (!matchesTab(strategy, strategyTypeFilter)) {
                return false;
            }
            // Shared allocations cannot contain personal strategies
            if (allocationType === AllocationType.SHARED && strategy.strategyType === StrategyType.PERSONAL) {
                return false;
            }
            return true;
        });
    }, [availableStrategies, strategyAllocations, strategyTypeFilter, allocationType]);

    return {
        strategyTypeFilter,
        setStrategyTypeFilter,
        availableStrategiesToAdd,
    };
}
